import React, { useEffect, useState } from "react";
import "../styles/Dashboard.scss";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  FaBriefcase,
  FaUsers,
  FaUserPlus,
  FaFileSignature,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { getAdminDashboard } from "../services/api";

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get user name from localStorage
  let username = "";
  try {
    const userData = JSON.parse(localStorage.getItem("userData"));
    username =
      userData?.user?.name ||
      userData?.userAuth?.name ||
      userData?.user?.email ||
      userData?.userAuth?.email ||
      "";
  } catch (e) {
    username = "";
  }

  // Dynamic summary stats from API
  const summaryStats = [
    {
      label: "Total Jobs",
      value: dashboardData?.summary_info?.total_jobs || 0,
      icon: <FaBriefcase size={22} className="me-2 text-primary" />,
    },
    {
      label: "Total Candidates",
      value: dashboardData?.summary_info?.total_candidates || 0,
      icon: <FaUsers size={22} className="me-2 text-success" />,
    },
    {
      label: "Offers Released",
      value: dashboardData?.summary_info?.offers_released || 0,
      icon: <FaFileSignature size={22} className="me-2 text-info" />,
    },
    {
      label: "Joined",
      value: dashboardData?.summary_info?.joined || 0,
      icon: <FaCheckCircle size={22} className="me-2 text-success" />,
    },
    {
      label: "Offers Declined",
      value: dashboardData?.summary_info?.offers_declined || 0,
      icon: <FaTimesCircle size={22} className="me-2 text-danger" />,
    },
    {
      label: "No Show",
      value: dashboardData?.summary_info?.no_show || 0,
      icon: <FaUserPlus size={22} className="me-2 text-warning" />,
    },
  ];

  // getDashboardDetails API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch dashboard data from API
        const dashboardRes = await getAdminDashboard();
        if (dashboardRes?.status === "S") {
          setDashboardData(dashboardRes.result_info);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Dynamic funnel data from API
  const funnelData = dashboardData?.status_info
    ? Object.entries(dashboardData.status_info).map(
        ([status, count], index) => ({
          status,
          count,
          color: [
            "#3b82f6",
            "#1d4ed8",
            "#10b981",
            "#ef4444",
            "#f59e0b",
            "#8b5cf6",
            "#06b6d4",
            "#ec4899",
            "#84cc16",
            "#f97316",
            "#6366f1",
            "#14b8a6",
            "#22c55e",
            "#6b7280",
            "#dc2626",
            "#7c2d12",
          ][index % 16],
        })
      )
    : [
        { status: "Internal Screen Select", count: 0, color: "#3b82f6" },       // blue-500
        { status: "Internal Screen Reject", count: 0, color: "#2563eb" },       // blue-600
        { status: "Sent to Client", count: 0, color: "#1d4ed8" },               // blue-700
        { status: "Client Screen Select", count: 0, color: "#10b981" },         // emerald-500
        { status: "Client Screen Reject", count: 0, color: "#059669" },         // emerald-600
        { status: "Requirement Hold", count: 0, color: "#dc2626" },             // red-600
        { status: "L1 Scheduled", count: 0, color: "#f59e0b" },                 // amber-500
        { status: "L1-Feedback Pending", count: 0, color: "#d946ef" },          // fuchsia-500
        { status: "L1-Select", count: 0, color: "#a855f7" },                    // violet-500
        { status: "L1-Reject", count: 0, color: "#7c3aed" },                    // violet-600
        { status: "L1-No Show", count: 0, color: "#6d28d9" },                   // violet-700
        { status: "L1-Requirement Hold", count: 0, color: "#5b21b6" },          // violet-800
        { status: "L2-Scheduled", count: 0, color: "#06b6d4" },                 // cyan-500
        { status: "L2-Feedback Pending", count: 0, color: "#0ea5e9" },          // sky-500
        { status: "L2-Select", count: 0, color: "#0284c7" },                    // sky-600
        { status: "L2-Reject", count: 0, color: "#0369a1" },                    // sky-700
        { status: "L2-No Show", count: 0, color: "#075985" },                   // sky-800
        { status: "L2-Requirement Hold", count: 0, color: "#0c4a6e" },          // sky-900
        { status: "Client Round Scheduled", count: 0, color: "#ec4899" },       // pink-500
        { status: "Client Round-Feedback Pending", count: 0, color: "#eab308" },// yellow-500
        { status: "Client Round-Select", count: 0, color: "#9333ea" },          // purple-600
        { status: "Client Round-Reject", count: 0, color: "#7e22ce" },          // purple-700
        { status: "Client Round-No Show", count: 0, color: "#6b21a8" },         // purple-800
        { status: "Client Round-Requirement Hold", count: 0, color: "#581c87" },// purple-900
        { status: "HR Discussion", count: 0, color: "#f97316" },                // orange-500
        { status: "BGV Inprogress", count: 0, color: "#8b5cf6" },               // violet-500
        { status: "Offer Inprogress", count: 0, color: "#22d3ee" },             // cyan-400
        { status: "Offer Released", count: 0, color: "#22c55e" },               // green-500
        { status: "Offer Accepted", count: 0, color: "#15803d" },               // green-800
        { status: "Offer Declined", count: 0, color: "#b91c1c" },               // red-700
        { status: "Joined", count: 0, color: "#7c2d12" },                       // orange-900
        { status: "Joining No Show", count: 0, color: "#78350f" },              // orange-800
      ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }
  return (
    <div className="admin-dashboard">
      {/* To display username based on role_id */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Welcome, {username || "User"}!</h1>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="row mb-4">
        {summaryStats.map((stat) => (
          <div className="col-lg-2 col-md-4 col-sm-6 col-12 mb-3 d-flex" key={stat.label}>
            <div
              className="card p-lg-2 d-flex flex-row justify-content-center w-100"
              style={{ minHeight: 110, height: "100%" }}
            >
              <div
                className="me-lg-1 d-flex align-items-center justify-content-center"
                style={{ fontSize: 34, minWidth: 40 }}
              >
                {React.cloneElement(stat.icon, { size: 35 })}
              </div>
              <div className="d-flex flex-column align-items-start justify-content-center">
                <h5 className="mb-0" style={{ fontWeight: 700 }}>
                  {stat.value}
                </h5>
                <div
                  className="text-muted"
                  style={{ fontSize: ".9rem", fontWeight: 500 }}
                >
                  {stat.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Status Funnel Bar Graph */}
      <div style={{overflow: 'auto'}}>
      <div className="card p-4 mb-4" style={{minWidth: '800px'}}>
        <h5 className="mb-3">Status Funnel</h5>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={funnelData}
            margin={{ left: 20, right: 20, top: 10, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="status"
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
            />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count" barSize={30}>
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      </div>
      
    </div>
  );
}

export default Dashboard;
